import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, LoginCredentials, RegisterData } from "../types/auth";
import authService from "../services/authService";
import { useToast } from "../hooks/use-toast";

type ContextValue = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<{ pendingUserId: string; email: string }>;
  verifySignupOtp: (email: string, code: string) => Promise<void>;

  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
refreshUserData: () => Promise<void>;
 
  requestResetOtp: (email: string) => Promise<void>;
  resetPasswordWithOtp: (email: string, code: string, password: string) => Promise<void>;
};

const AuthContext = createContext<ContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        const accessToken = authService.getAccessToken();

        if (storedUser && accessToken) {
          try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
          } catch {
            try {
              await authService.refreshAccessToken();
              const currentUser = await authService.getCurrentUser();
              setUser(currentUser);
            } catch {
              await authService.logout();
              setUser(null);
            }
          }
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.login(credentials);
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      toast({
        title: "Login Successful",
        description: `Welcome back, ${currentUser.firstName}!`,
      });
    } catch (error: any) {
      const code = String(error?.code || error?.message || "").toUpperCase();
      if (code === "EMAIL_NOT_VERIFIED") {
        throw error; // caller (Login page) handles redirect + loading UI
      }

      const errorMessage = error instanceof Error ? error.message : "Login failed";
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    data: RegisterData
  ): Promise<{ pendingUserId: string; email: string }> => {
    try {
      setIsLoading(true);
      const pending = await authService.register(data);
      toast({
        title: "Verify Your Email",
        description: "We sent a 6-digit code to your inbox.",
      });
      return pending;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Registration failed";
      toast({
        title: "Registration Failed",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // verify signup OTP
  const verifySignupOtp = async (email: string, code: string): Promise<void> => {
    try {
      setIsLoading(true);
      const authResp = await authService.verifySignupOtp(email, code);
      setUser(authResp.user);
      toast({ title: "Email Verified", description: "Welcome aboard! 🎉" });
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Verification failed";
      toast({ title: "Invalid Code", description: msg, variant: "destructive" });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out.",
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      await authService.refreshAccessToken();
    } catch (error) {
      await logout();
      throw error;
    }
  };

  const updateProfile = async (data: Partial<User>): Promise<void> => {
    try {
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Profile update failed";
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    try {
      await authService.changePassword({ currentPassword, newPassword });
      toast({
        title: "Password Changed",
        description: "Your password has been successfully changed.",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Password change failed";
      toast({
        title: "Password Change Failed",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

   const refreshUserData = async (): Promise<void> => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refreshing user data fails, it might mean the token is invalid
      // Let's try to refresh the token and try again
      try {
        await authService.refreshAccessToken();
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (refreshError) {
        console.error('Failed to refresh user data after token refresh:', refreshError);
        // Don't throw the error here as this might be called in background
      }
    }
  };

  // forgot/reset via OTP
  const requestResetOtp = async (email: string): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.forgotPasswordOtp(email);
      toast({
        title: "Code Sent",
        description: "Check your email for the 6-digit code.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordWithOtp = async (
    email: string,
    code: string,
    password: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.resetPasswordWithOtp(email, code, password);
      toast({
        title: "Password Reset",
        description: "You can now sign in with your new password.",
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Reset failed";
      toast({ title: "Reset Failed", description: msg, variant: "destructive" });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: ContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    verifySignupOtp,
    logout,
    refreshToken,
    updateProfile,
    changePassword,
    requestResetOtp,
    resetPasswordWithOtp,
    refreshUserData,
  };
  // Automatically toggle scroll behavior when auth state changes
   useEffect(() => {
   if (user) {
    // Logged in → prevent double scrollbar
      document.body.classList.add('app-logged-in');
    } else {
      // Logged out → restore normal scrolling for landing pages
      document.body.classList.remove('app-logged-in');
    }
  }, [user]);


  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): ContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export default AuthContext;
